import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import "./styles/index.scss";
import ReactDOM from "react-dom";
import { RouterProvider } from "react-router-dom";
import { Global, ThemeProvider } from "@emotion/react";
import { Toaster } from "react-hot-toast";
import { LicenseInfo } from "@mui/x-license-pro";
import { buildAppTheme } from "./theme";
import { TOKENS, themeGlobalStyles } from "./theme/tokens";
import { routes } from "./router";
import { DownloadsProvider } from "./components/Downloads";
import { useBoundStore } from "./store";
import { getEffectiveThemeMode } from "./store/theme";
import posthog from "posthog-js";
import config from "./config";

if (config.POSTHOG_KEY) {
    posthog.init(config.POSTHOG_KEY as string, {
        api_host: config.POSTHOG_HOST as string,
        // dated defaults capture pageviews on history changes,
        // which SPA route transitions need
        defaults: "2026-05-30",
        person_profiles: "identified_only"
    });
    // Stamp every event (exceptions included) with the same environment /
    // service properties the API attaches server-side, so staging and
    // production stay separable in analytics and error tracking.
    posthog.register({
        service: "tdc-portals",
        environment: process.env.REACT_APP_ENV || "development"
    });
}

//LicenseInfo.setLicenseKey('b09dc2a2b24042c11d805a1c3b173887Tz03MDgwOCxFPTE3MjEwNjk5NDAwMDAsUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI=');
LicenseInfo.setLicenseKey(
    "f81428571a53b58dec568a58dcf755e3Tz0xMTEwODcsRT0xNzc1NjkyNzk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1pbml0aWFsLEtWPTI="
);

function LoadTidio() {
    useEffect(() => {
        //if (process.env.NODE_ENV !== 'production') return; // optional: only in production
        const s = document.createElement("script");
        s.src = "//code.tidio.co/xx3vyxw546opsc2pgud1gf44qyfvtn1y.js";
        s.async = true;
        document.body.appendChild(s);
        return () => {
            document.body.removeChild(s);
        };
    }, []);
    return null;
}

// add Google gtag loader
function LoadGtag() {
    useEffect(() => {
        const s = document.createElement("script");
        s.async = true;
        s.src = "https://www.googletagmanager.com/gtag/js?id=G-XVRZN4BGVQ";
        document.head.appendChild(s);

        const inline = document.createElement("script");
        inline.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-XVRZN4BGVQ');
    `;
        document.head.appendChild(inline);

        return () => {
            if (s.parentNode) s.parentNode.removeChild(s);
            if (inline.parentNode) inline.parentNode.removeChild(inline);
        };
    }, []);
    return null;
}

// Single writer of theme state: computes the effective mode (user preference
// gated by dark-eligible scopes — see src/store/theme.ts), owns the
// data-theme attribute on <html> (components must NEVER set it themselves),
// picks the generated MUI theme, and keeps the browser theme-color in sync.
function ThemeRoot({ children }: { children: React.ReactNode }) {
    const effectiveMode = useBoundStore(getEffectiveThemeMode);
    const muiTheme = useMemo(() => buildAppTheme(effectiveMode), [effectiveMode]);
    // Animate the user's PREFERENCE changing, never the effective mode. On every
    // load the effective mode necessarily starts light (no dark scope is
    // registered until AppShell's layout effect runs), so keying the transition
    // off effectiveMode cross-faded the whole app light->dark on each page load
    // for dark-mode users. Entering or leaving a dark scope must be instant.
    const preference = useBoundStore((s) => s.themeMode);
    const prevPreference = useRef(preference);

    useLayoutEffect(() => {
        const root = document.documentElement;
        if (effectiveMode === "dark") root.setAttribute("data-theme", "dark");
        else root.removeAttribute("data-theme");

        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta) meta.setAttribute("content", TOKENS[effectiveMode].topbar);

        // 220ms cross-fade, scoped in themeGlobalStyles to color-bearing properties
        // and reduced-motion aware.
        if (prevPreference.current !== preference) {
            prevPreference.current = preference;
            root.classList.add("theme-switching");
            const timer = window.setTimeout(() => root.classList.remove("theme-switching"), 260);
            return () => {
                window.clearTimeout(timer);
                root.classList.remove("theme-switching");
            };
        }
        return undefined;
    }, [effectiveMode, preference]);

    return (
        <ThemeProvider theme={muiTheme}>
            <Global styles={themeGlobalStyles} />
            {children}
        </ThemeProvider>
    );
}

ReactDOM.render(
    <ThemeRoot>
        <LoadTidio />
        <LoadGtag />
        {/* Above the router on purpose: the download tracker (and the app's
        single socket connection it owns) must survive navigation and exist
        on every route, including the public share pages. */}
        <DownloadsProvider>
            <RouterProvider router={routes} />
        </DownloadsProvider>
        <Toaster
            position="top-right"
            toastOptions={{
                style: {
                    padding: "16px",
                    fontSize: "18px",
                    background: "var(--surface)",
                    color: "var(--text)"
                },
                duration: 3000
            }}
        />
    </ThemeRoot>,
    document.getElementById("root")
);
